/**
 * Fetch mock utilities
 * @module @orchard9ai/testing-utils/mocks/fetch
 */

import type { ApiResponse, ApiErrorResponse } from '@orchard9ai/types';

export interface MockFetchOptions {
  /** Response data */
  data?: any;
  /** HTTP status code */
  status?: number;
  /** Response headers */
  headers?: Record<string, string>;
  /** Delay in milliseconds */
  delay?: number;
  /** Should reject */
  reject?: boolean;
  /** Error to throw */
  error?: Error;
}

/**
 * Mock fetch implementation
 */
export class MockFetch {
  private mocks: Map<string, MockFetchOptions> = new Map();
  private defaultMock?: MockFetchOptions;
  private calls: Array<{ url: string; options: RequestInit | undefined }> = [];

  /**
   * Set default mock response
   */
  setDefault(options: MockFetchOptions): void {
    this.defaultMock = options;
  }

  /**
   * Mock specific URL
   */
  mockUrl(url: string | RegExp, options: MockFetchOptions): void {
    this.mocks.set(url.toString(), options);
  }

  /**
   * Mock API response
   */
  mockApiResponse<T>(url: string | RegExp, data: T, options?: Partial<MockFetchOptions>): void {
    const response: ApiResponse<T> = {
      data,
      timestamp: new Date().toISOString(),
    };

    this.mockUrl(url, {
      data: response,
      status: 200,
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
  }

  /**
   * Mock API error
   */
  mockApiError(
    url: string | RegExp,
    error: ApiErrorResponse,
    options?: Partial<MockFetchOptions>
  ): void {
    this.mockUrl(url, {
      data: error,
      status: error.error.code === 'NOT_FOUND' ? 404 : 400,
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
  }

  /**
   * Get fetch implementation
   */
  getFetch(): typeof fetch {
    return async (url: string | URL | Request, init?: RequestInit) => {
      const urlString = url.toString();
      this.calls.push({ url: urlString, options: init });

      // Find matching mock
      let mockOptions: MockFetchOptions | undefined;

      for (const [pattern, options] of this.mocks.entries()) {
        if (pattern.startsWith('/') && pattern.endsWith('/')) {
          // Regex pattern
          const regex = new RegExp(pattern.slice(1, -1));
          if (regex.test(urlString)) {
            mockOptions = options;
            break;
          }
        } else if (urlString === pattern || urlString.includes(pattern)) {
          mockOptions = options;
          break;
        }
      }

      // Use default if no match
      if (!mockOptions) {
        mockOptions = this.defaultMock || {
          data: null,
          status: 404,
          headers: {},
        };
      }

      // Add delay if specified
      if (mockOptions.delay) {
        await new Promise((resolve) => setTimeout(resolve, mockOptions.delay));
      }

      // Reject if specified
      if (mockOptions.reject || mockOptions.error) {
        throw mockOptions.error || new Error('Network error');
      }

      // Create response
      const response = new Response(mockOptions.data ? JSON.stringify(mockOptions.data) : null, {
        status: mockOptions.status || 200,
        headers: new Headers(mockOptions.headers || {}),
      });

      return response;
    };
  }

  /**
   * Get all calls
   */
  getCalls(): Array<{ url: string; options: RequestInit | undefined }> {
    return [...this.calls];
  }

  /**
   * Get calls for specific URL
   */
  getCallsFor(url: string | RegExp): Array<{ url: string; options: RequestInit | undefined }> {
    const pattern = url.toString();

    return this.calls.filter((call) => {
      if (pattern.startsWith('/') && pattern.endsWith('/')) {
        const regex = new RegExp(pattern.slice(1, -1));
        return regex.test(call.url);
      }
      return call.url === pattern || call.url.includes(pattern);
    });
  }

  /**
   * Assert URL was called
   */
  assertCalled(url: string | RegExp, times?: number): void {
    const calls = this.getCallsFor(url);

    if (times !== undefined) {
      if (calls.length !== times) {
        throw new Error(
          `Expected ${url} to be called ${times} times, but was called ${calls.length} times`
        );
      }
    } else if (calls.length === 0) {
      throw new Error(`Expected ${url} to be called, but it was not`);
    }
  }

  /**
   * Assert URL was not called
   */
  assertNotCalled(url: string | RegExp): void {
    const calls = this.getCallsFor(url);

    if (calls.length > 0) {
      throw new Error(`Expected ${url} not to be called, but it was called ${calls.length} times`);
    }
  }

  /**
   * Reset mocks
   */
  reset(): void {
    this.mocks.clear();
    this.calls = [];
    delete this.defaultMock;
  }
}

/**
 * Create mock fetch instance
 */
export function createMockFetch(): MockFetch {
  return new MockFetch();
}

/**
 * Install mock fetch globally
 */
export function installMockFetch(mockFetch: MockFetch): void {
  (global as any).fetch = mockFetch.getFetch();
}

/**
 * Restore original fetch
 */
export function restoreFetch(): void {
  delete (global as any).fetch;
}
